import { useMutation } from '@apollo/client'
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Title, useNotify, useTranslate } from 'react-admin'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CONNECT, redirectUri, takeState } from './instagram'

/**
 * Where Instagram sends the person back after "Mit Instagram verbinden":
 * `?code=…&state=…` (or `?error=…`). The state must be the one this browser
 * started the login with; the code is used once, then the page moves on so it
 * does not stay in the history.
 */
export const InstagramCallback = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [connect] = useMutation(CONNECT)
  const [error, setError] = useState<string | null>(null)
  // A code is good for one exchange; StrictMode would run the effect twice.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const denied = params.get('error_description') || params.get('error')
    if (denied) {
      setError(translate('social.instagram.denied', { reason: denied }))
      return
    }
    const code = params.get('code')
    const state = params.get('state')
    const expected = takeState()
    if (!code || !state || state !== expected) {
      setError(translate('social.instagram.bad_state'))
      return
    }
    connect({ variables: { code, redirect_uri: redirectUri() } })
      .then(({ data }) => {
        notify('social.instagram.connected_as', {
          type: 'success',
          messageArgs: { username: data?.instagram_connect?.username },
        })
        navigate('/social#instagram', { replace: true })
      })
      .catch((e) => setError(e.message))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 2 }}>
      <Title title={translate('social.instagram.title')} />
      {error ? (
        <>
          <Alert severity='error' sx={{ mb: 2 }} data-testid='instagram-callback-error'>
            {error}
          </Alert>
          <Button component={Link} to='/social' variant='outlined'>
            {translate('social.instagram.back')}
          </Button>
        </>
      ) : (
        <Stack direction='row' spacing={1.5} sx={{ alignItems: 'center' }}>
          <CircularProgress size={22} />
          <Typography>{translate('social.instagram.connecting')}</Typography>
        </Stack>
      )}
    </Box>
  )
}

export default InstagramCallback
